import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, Check, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { locations } from '../mock';

const LocationDetailPage = () => {
  const { slug } = useParams();
  const loc = locations.find((l) => l.slug === slug);
  if (!loc) return <Navigate to="/locations" replace />;
  const others = locations.filter((l) => l.slug !== slug);

  return (
    <div className="App bg-white">
      <Header />

      <section className="max-w-[1240px] mx-auto px-6 pt-6">
        <div className="relative rounded-[28px] overflow-hidden min-h-[420px]">
          <img src={loc.image} alt={loc.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="relative z-10 px-8 lg:px-14 pt-14 pb-14">
            <div className="flex items-center gap-2 text-[12px] text-white/80">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>›</span>
              <Link to="/locations" className="hover:text-white">Locations</Link>
              <span>›</span>
              <span className="text-white font-medium">{loc.name}</span>
            </div>
            <h1 className="mt-10 text-[42px] lg:text-[64px] leading-[0.98] font-bold tracking-[-0.03em] text-white max-w-2xl">
              {loc.name}
            </h1>
            <p className="mt-4 text-white/85 text-[16px] max-w-xl">{loc.address}</p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-[1fr_360px] gap-14">
          <div>
            <h2 className="text-[32px] lg:text-[42px] leading-[1.02] font-bold tracking-tight text-neutral-900">
              About this <span className="font-serif-italic font-normal">clinic</span>
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed text-neutral-700">
              Our {loc.name} sits at the heart of the community and offers the full Uptown Physiotherapy Clinic
              standard of physiotherapy, athletic therapy, massage, and rehabilitation services. Ample
              parking, a bright waiting area, and a friendly reception team make every visit easy.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {loc.features.map((f) => (
                <div key={f} className="flex items-start gap-3 rounded-2xl border border-neutral-200 p-5">
                  <span className="w-8 h-8 rounded-full bg-[#CA0028]/10 grid place-items-center shrink-0">
                    <Check size={14} className="text-[#CA0028]" />
                  </span>
                  <div>
                    <div className="font-semibold text-neutral-900 text-[15px]">{f}</div>
                    <div className="text-[13px] text-neutral-500 mt-1">Included at this location</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 rounded-2xl overflow-hidden border border-neutral-200 aspect-[16/9] bg-neutral-100">
              <iframe
                title="Map"
                className="w-full h-full"
                src={`https://www.google.com/maps?q=${encodeURIComponent(loc.address)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <aside className="lg:pt-6">
            <div className="rounded-2xl border border-neutral-200 p-6 bg-neutral-50">
              <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Visit us</h3>
              <div className="mt-4 space-y-3 text-[14px] text-neutral-800">
                <p className="flex items-start gap-3"><MapPin size={16} className="mt-1 text-neutral-500" /> {loc.address}</p>
                <p className="flex items-center gap-3"><Phone size={16} className="text-neutral-500" /> {loc.phone}</p>
                <p className="flex items-center gap-3"><Mail size={16} className="text-neutral-500" /> {loc.email}</p>
                <p className="flex items-center gap-3"><Clock size={16} className="text-neutral-500" /> {loc.hours}</p>
              </div>
              <a href="tel:+15192522752" className="mt-6 inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                Call to Book
                <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15"><ArrowRight size={14} /></span>
              </a>
            </div>

            <div className="mt-6">
              <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Other clinics</h3>
              <ul className="mt-3 divide-y divide-neutral-200">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to={`/locations/${o.slug}`} className="flex items-center justify-between py-3 text-[14px] text-neutral-900 hover:text-[#CA0028] transition-colors">
                      {o.name}
                      <ArrowRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationDetailPage;
